"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { useIntlayer } from "next-intlayer";

const SYMBOLS = ["123", "x²", "△", "sin", "f(x)", "∫", "P", "xy"];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.33, 1, 0.68, 1] as const },
  },
};

export default function MarqueeSection() {
  const content = useIntlayer("marquee-section");

  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          className="mb-14"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <p className="text-xs font-semibold tracking-[0.3em] uppercase mb-4 text-[#0193cd]">
            {content.label}
          </p>
          <h2 className="text-4xl md:text-5xl font-semibold text-foreground">{content.heading}</h2>
          <p className="mt-3 text-base text-muted-foreground">{content.subheading}</p>
        </motion.div>

        {/* 3×3 grid: 8 topic cards + 1 "and more" card */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          style={{ gridAutoRows: "1fr" }}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
        >
          {/* Topic cards */}
          {content.topics.map((topic, i) => (
            <motion.div
              // biome-ignore lint/suspicious/noArrayIndexKey: static topics array
              key={i}
              variants={cardVariants}
              whileHover={{
                y: -6,
                boxShadow: "0 20px 40px rgba(1, 147, 205, 0.12)",
                transition: { duration: 0.2 },
              }}
              className="relative overflow-hidden rounded-2xl bg-card p-8 cursor-default border border-border flex flex-col justify-between min-h-[200px]"
            >
              {/* Decorative symbol */}
              <span className="absolute -right-4 -bottom-6 text-[9rem] font-bold leading-none select-none pointer-events-none text-primary/10">
                {SYMBOLS[i]}
              </span>

              {/* Accent bar */}
              <motion.div
                className="absolute top-0 left-0 w-1 rounded-r-full bg-[#0193cd]"
                initial={{ height: 0 }}
                whileInView={{ height: "40%" }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
              />

              <div className="relative z-10">
                <h3 className="text-xl font-semibold mb-3 text-foreground">{topic.name}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{topic.desc}</p>
              </div>
            </motion.div>
          ))}

          {/* "And more" card */}
          <motion.div
            variants={cardVariants}
            whileHover={{
              y: -6,
              boxShadow: "0 20px 40px rgba(1, 110, 164, 0.25)",
              transition: { duration: 0.2 },
            }}
            className="relative overflow-hidden rounded-2xl min-h-[200px] flex flex-col items-center justify-center p-8 cursor-pointer border-2 border-dashed border-[#0193cd] bg-primary/5"
          >
            {/* Animated ring */}
            <motion.div
              className="absolute w-40 h-40 rounded-full border border-[#0193cd] opacity-10"
              animate={{ scale: [1, 1.3, 1], opacity: [0.1, 0.25, 0.1] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="absolute w-28 h-28 rounded-full border border-[#0193cd] opacity-15"
              animate={{ scale: [1, 1.4, 1], opacity: [0.15, 0.3, 0.15] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
            />

            <div className="relative z-10 text-center">
              <motion.p
                className="text-5xl font-bold mb-4 leading-none text-[#0193cd]"
                animate={{ opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
              >
                +
              </motion.p>
              <p className="text-lg font-semibold mb-2 text-[#0193cd]">{content.andManyMore}</p>
              <p className="text-sm mb-5 text-muted-foreground">{content.andManyMoreDesc}</p>
              <Link
                href="/courses"
                className="inline-block px-5 py-2 rounded-full text-sm font-semibold text-white transition-all hover:opacity-90 bg-[#0193cd]"
              >
                {content.browseAllCourses}
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
